import {
  PraksisaktivitetAvtalerDto,
  PraksisavtaleIdToPraksisstedIderDto,
  PraksisavtaleStedBestillingspunktTypeDto,
  PraksisavtaleStedDto,
} from '../../api/generated/studiested-generated-api';
import mapNullsToUndefined, {
  mapNullsToUndefinedOneDimensional,
} from '../../util/mapNullsToUndefined';
import { PraksisavtaleStedDtoKapasitetstypeDtoEnum } from '../../api/generated/praksissted-generated-api';
import {
  AgreementsAction,
  AgreementsActionType,
  AgreementTableContextProps,
  AgreementTableRowProps,
  AppliedToAllUndernoderForStedAvtaleBestilling,
  ApplyToAllUndernoderKey,
  Expanded,
  FlattenedAvtaleSted,
  FlattenedAvtaleSteder,
  NearestAvtaleKeyParent,
  PraksisavtaleSted,
  StedAvtaleBestilling,
  StedAvtaleBestillinger,
  StedWithUndernoderAndBestillingspunkt,
} from './types';
import React, { Reducer } from 'react';
import useContextNotUndefined from '../../hooks/useContextNotUndefined';

export const getAvtaleKey = (avtaleId: string, praksisstedId: string) => {
  return avtaleId + '|' + praksisstedId;
};

export const praksisstedIdFromAvtaleKey = (key: string) => key.split('|')[1];
export const avtaleIdFromAvtaleKey = (key: string) => key.split('|')[0];

export const getExpandedStateFromSessionStorage = (
  activityId: string,
): Expanded | undefined =>
  JSON.parse(
    sessionStorage.getItem(`expandedStederInActivity-v2-${activityId}`) ||
      'null',
  ) || undefined;

export const putExpandedStateInSessionStorage = (
  activityId: string,
  expanded: Expanded,
) =>
  sessionStorage.setItem(
    `expandedStederInActivity-v2-${activityId}`,
    JSON.stringify(expanded),
  );

export const getTableScrollTopFromSessionStorage = (
  activityId: string,
): number =>
  parseInt(
    sessionStorage.getItem(`scrollStederTableInActivity-${activityId}`) || '0',
  );

export const putTableScrollTopInSessionStorage = (
  activityId: string,
  num: number,
) =>
  sessionStorage.setItem(
    `scrollStederTableInActivity-${activityId}`,
    num.toString(),
  );

export const getWindowScrollTopFromSessionStorage = (
  activityId: string,
): number =>
  parseInt(
    sessionStorage.getItem(`scrollStederWindowInActivity-${activityId}`) || '0',
  );

export const putWindowScrollTopInSessionStorage = (
  activityId: string,
  num: number,
) =>
  sessionStorage.setItem(
    `scrollStederWindowInActivity-${activityId}`,
    num.toString(),
  );

export const hasBestillingOnChildren = (
  nodes: PraksisavtaleStedDto[],
): boolean =>
  nodes.some(
    (node) =>
      (node.bestilling !== undefined &&
        node.bestilling !== null &&
        node.bestilling >= 0) ||
      hasBestillingOnChildren(node.undernoder),
  );

export const flattenAvtaler = (
  avtaler: PraksisaktivitetAvtalerDto[] | undefined,
): FlattenedAvtaleSteder =>
  avtaler?.reduce(
    (acc: FlattenedAvtaleSteder, avtale: PraksisaktivitetAvtalerDto) => {
      const { avtaleId, praksissted } = avtale;
      const {
        bestilling,
        kapasitet,
        bestillingspunkt,
        kapasitetspunkt,
        kapasitetstype,
        overnoder,
        overnoderOgDeresSosken,
        soskennoder,
        undernoder,
        navn,
        valideringsfeil,
      } = mapNullsToUndefinedOneDimensional(praksissted);

      return {
        ...acc,
        [getAvtaleKey(avtaleId, praksissted.id)]: {
          bestilling,
          kapasitet,
          bestillingspunkt,
          kapasitetspunkt,
          kapasitetstype,
          overnoder,
          overnoderOgDeresSosken,
          soskennoder,
          undernoder,
          navn,
          valideringsfeil,
        },
        ...flattenAvtaleSteder(undernoder, avtaleId),
      };
    },
    {},
  ) || {};

export const flattenAvtaleSteder = (
  avtaleSteder: PraksisavtaleStedDto[] | undefined,
  avtaleId: string,
): FlattenedAvtaleSteder =>
  avtaleSteder?.reduce(
    (acc: FlattenedAvtaleSteder, avtaleSted: PraksisavtaleStedDto) => {
      const {
        bestilling,
        kapasitet,
        bestillingspunkt,
        kapasitetspunkt,
        kapasitetstype,
        overnoder,
        overnoderOgDeresSosken,
        soskennoder,
        undernoder,
        navn,
        valideringsfeil,
      } = mapNullsToUndefinedOneDimensional(avtaleSted);

      return {
        ...acc,
        [getAvtaleKey(avtaleId, avtaleSted.id)]: {
          bestilling,
          kapasitet,
          bestillingspunkt,
          kapasitetspunkt,
          kapasitetstype,
          overnoder,
          overnoderOgDeresSosken,
          soskennoder,
          undernoder,
          navn,
          valideringsfeil,
        },
        ...flattenAvtaleSteder(undernoder, avtaleId),
      };
    },
    {},
  ) || {};

export const AgreementsReducer: Reducer<
  StedAvtaleBestillinger,
  AgreementsAction
> = (state, action) => {
  if (action.type === AgreementsActionType.UPDATE) {
    // Will allow for only updating specified values for specified keys. Remaining state is unchanged.
    return Object.entries(action.payload).reduce(
      (acc, [avtaleKey, stedAvtaleBestilling]) => ({
        ...acc,
        [avtaleKey]: {
          ...state[avtaleKey],
          ...stedAvtaleBestilling,
        },
      }),
      state,
    );
  }

  return { ...state };
};

const overnodeIsDeaktivertOrSkjult = (
  overnodeId: string,
  deaktiverteOrSkjulteSteder: PraksisavtaleIdToPraksisstedIderDto[],
  avtaleKey: string,
): boolean =>
  avtaleKeyInListOfPraksisavtaleIdToPraksisstedIderDto(
    getAvtaleKey(avtaleIdFromAvtaleKey(avtaleKey), overnodeId),

    deaktiverteOrSkjulteSteder,
  );

export const getInitAvtaler = (
  avtaler: FlattenedAvtaleSteder,
  deaktiverteSteder: PraksisavtaleIdToPraksisstedIderDto[],
  skjulteSteder: PraksisavtaleIdToPraksisstedIderDto[],
  harKapasitetspunktOver: string[],
): StedAvtaleBestillinger =>
  Object.entries(avtaler).reduce((acc, [avtaleKey, flattenedSted]) => {
    const {
      bestilling,
      kapasitet,
      kapasitetstype,
      kapasitetspunkt,
      overnoder,
    } = mapNullsToUndefined(flattenedSted) as {
      bestilling: number | undefined;
      kapasitet: number | undefined;
      kapasitetstype: PraksisavtaleStedDtoKapasitetstypeDtoEnum | undefined;
      kapasitetspunkt: boolean;
      overnoder: string[];
    };

    const overnodeIsDeaktivert = (overnodeId: string) =>
      overnodeIsDeaktivertOrSkjult(overnodeId, deaktiverteSteder, avtaleKey);
    const overnodeIsSkjult = (overnodeId: string) =>
      overnodeIsDeaktivertOrSkjult(overnodeId, skjulteSteder, avtaleKey);

    const stedAvtaleBestilling: StedAvtaleBestilling = {
      valideringsfeil: undefined,
      bestilling,
      kapasitet,
      kapasitetstype,
      kapasitetspunkt,
      deaktivertAvForelderKapasitet: harKapasitetspunktOver.includes(avtaleKey),
      deaktivert: avtaleKeyInListOfPraksisavtaleIdToPraksisstedIderDto(
        avtaleKey,
        deaktiverteSteder,
      ),
      skjult: avtaleKeyInListOfPraksisavtaleIdToPraksisstedIderDto(
        avtaleKey,
        skjulteSteder,
      ),
      hasDeaktivertForelder: overnoder.some(overnodeIsDeaktivert),
      hasSkjultForelder: overnoder.some(overnodeIsSkjult),
    };

    return {
      ...acc,
      [avtaleKey]: stedAvtaleBestilling,
    };
  }, {});

const getFordelingerForAvtale = (
  avtaleId: string,
  fordeltePraksissteder: PraksisavtaleIdToPraksisstedIderDto[],
) =>
  fordeltePraksissteder.find(
    ({ avtaleId: fordelingerAvtaleId }) => fordelingerAvtaleId === avtaleId,
  )?.praksisstedIder;

const anyOvernodeOrSiblingWithFordeling = (
  fordeltePraksissteder: string[],
  overnoderMedSosken: string[],
  soskennoder: string[],
) =>
  overnoderMedSosken
    .concat(soskennoder)
    .some((praksisstedId) => fordeltePraksissteder.includes(praksisstedId));

const anyUndernodeInTarget = (
  targetPraksissteder: string[],
  undernoder: PraksisavtaleStedDto[],
  avtaleId: string,
) =>
  Object.keys(flattenAvtaleSteder(undernoder, avtaleId)).some((avtaleKey) =>
    targetPraksissteder.includes(praksisstedIdFromAvtaleKey(avtaleKey)),
  );

export const anyOvernodeOrSiblingOrUndernodeInTarget = (
  targetPraksissteder: PraksisavtaleIdToPraksisstedIderDto[],
  undernoder: PraksisavtaleStedDto[],
  overnoderMedSosken: string[],
  soskennoder: string[],
  avtaleId: string,
) => {
  const fordeltePraksisstederForAvtale =
    getFordelingerForAvtale(avtaleId, targetPraksissteder) || [];

  return (
    anyOvernodeOrSiblingWithFordeling(
      fordeltePraksisstederForAvtale,
      overnoderMedSosken,
      soskennoder,
    ) ||
    anyUndernodeInTarget(fordeltePraksisstederForAvtale, undernoder, avtaleId)
  );
};

export const anyOmfattetAvtaleStedUndernode = (
  noder: StedWithUndernoderAndBestillingspunkt[],
  onlyFirstDecendant?: boolean,
): boolean =>
  noder.some(({ bestillingspunkt, undernoder }) => {
    const isOmfattetAvtaleSted =
      bestillingspunkt ===
      PraksisavtaleStedBestillingspunktTypeDto.OmfattetAvtaleSted;

    if (onlyFirstDecendant) {
      return isOmfattetAvtaleSted;
    }

    return isOmfattetAvtaleSted || anyOmfattetAvtaleStedUndernode(undernoder);
  });

export const getDeaktivertAvForelderKapasitetFromAvtaler = (
  avtaler: PraksisaktivitetAvtalerDto[] | undefined,
  updatedKapasitetspunkter?: string[],
) =>
  avtaler?.reduce((acc: string[], avtale: PraksisaktivitetAvtalerDto) => {
    const flattenedUndernoder = flattenAvtaleSteder(
      [avtale.praksissted],
      avtale.avtaleId,
    );

    const kapasitetspunkter =
      updatedKapasitetspunkter ||
      Object.entries(flattenedUndernoder)
        .filter(([_, { kapasitetspunkt }]) => kapasitetspunkt)
        .map(([avtaleKey]) => praksisstedIdFromAvtaleKey(avtaleKey));

    const deaktiverteUndernoder = Object.entries(flattenedUndernoder)
      .filter(([_, { overnoder }]) =>
        overnoder.some((overnodeId) => kapasitetspunkter.includes(overnodeId)),
      )
      .map(([avtaleKey]) => avtaleKey);

    return acc.concat(deaktiverteUndernoder);
  }, []) || [];

const mapAvtaleStedToNearestParent = (
  parentAvtaleKey: string,
  avtaleSteder: PraksisavtaleStedDto[],
): NearestAvtaleKeyParent =>
  avtaleSteder?.reduce(
    (acc: NearestAvtaleKeyParent, sted: PraksisavtaleStedDto) => {
      const avtaleKey = getAvtaleKey(
        avtaleIdFromAvtaleKey(parentAvtaleKey),
        sted.id,
      );

      return {
        ...acc,
        [avtaleKey]: parentAvtaleKey,
        ...mapAvtaleStedToNearestParent(avtaleKey, sted.undernoder),
      };
    },
    {},
  ) || {};

export const mapAvtalerToNearestParent = (
  avtaler: PraksisaktivitetAvtalerDto[] | undefined,
): NearestAvtaleKeyParent =>
  avtaler?.reduce(
    (acc: NearestAvtaleKeyParent, avtale: PraksisaktivitetAvtalerDto) => {
      const { avtaleId, praksissted } = avtale;
      const avtaleKey = getAvtaleKey(avtaleId, praksissted.id);

      return {
        ...acc,
        [avtaleKey]: avtaleKey,
        ...mapAvtaleStedToNearestParent(avtaleKey, praksissted.undernoder),
      };
    },
    {},
  ) || {};

const expandUndernoderUntilAnyUndernodeIsOmfattetSted = (
  avtaleId: string,
  undernoder: PraksisavtaleStedDto[],
): Expanded =>
  undernoder.reduce((acc: Expanded, sted: PraksisavtaleStedDto) => {
    if (
      sted.bestillingspunkt ===
      PraksisavtaleStedBestillingspunktTypeDto.OmfattetAvtaleSted
    ) {
      return acc;
    }

    if (anyOmfattetAvtaleStedUndernode(sted.undernoder, true)) {
      return { ...acc, [getAvtaleKey(avtaleId, sted.id)]: true };
    }

    return {
      ...acc,
      [getAvtaleKey(avtaleId, sted.id)]: true,
      ...expandUndernoderUntilAnyUndernodeIsOmfattetSted(
        avtaleId,
        sted.undernoder,
      ),
    };
  }, {});

export const getInitExpandedAvtaleHierarki = (
  avtaler: PraksisaktivitetAvtalerDto[] | undefined,
): Expanded =>
  avtaler?.reduce((acc: Expanded, avtale: PraksisaktivitetAvtalerDto) => {
    return avtale.bestillingsmodus ===
      PraksisavtaleStedBestillingspunktTypeDto.OmfattetAvtaleSted &&
      anyOmfattetAvtaleStedUndernode(avtale.praksissted.undernoder)
      ? {
          ...acc,
          [getAvtaleKey(avtale.avtaleId, avtale.praksissted.id)]: true,
          ...expandUndernoderUntilAnyUndernodeIsOmfattetSted(
            avtale.avtaleId,
            avtale.praksissted.undernoder,
          ),
        }
      : acc;
  }, {}) || {};

export const mapAvtaleIdToNewAvtaleKeysForPraksisstedIder = (
  avtaleKey: string,
  praksisstedIder: string[],
): string[] => {
  const avtaleId = avtaleIdFromAvtaleKey(avtaleKey);

  return praksisstedIder.map((praksisstedId) =>
    getAvtaleKey(avtaleId, praksisstedId),
  );
};

export const applyToAllUndernoderForStedAvtaleBestilling = (
  avtaleId: string,
  steder: PraksisavtaleStedDto[] | undefined,
  objectToApply: { [key in ApplyToAllUndernoderKey]?: unknown },
): AppliedToAllUndernoderForStedAvtaleBestilling =>
  steder?.reduce(
    (acc, sted) => ({
      ...acc,
      [getAvtaleKey(avtaleId, sted.id)]: objectToApply,
      ...applyToAllUndernoderForStedAvtaleBestilling(
        avtaleId,
        sted.undernoder,
        objectToApply,
      ),
    }),
    {},
  ) || {};

export const collapseAllUndernoder = (
  avtaleId: string,
  steder: PraksisavtaleStedDto[] | undefined,
): { [avtaleKey: string]: boolean } =>
  steder?.reduce((acc, sted) => {
    const avtaleKey = getAvtaleKey(avtaleId, sted.id);

    return {
      ...acc,
      [avtaleKey]: false,
      ...collapseAllUndernoder(avtaleId, sted.undernoder),
    };
  }, {}) || {};

const processRows = (
  rows: PraksisavtaleSted[] | undefined,
  indent: number,
  avtaleId: string,
  bestillingIsAtTop: boolean,
  avtalenavn: string | undefined,
): PraksisavtaleSted[] => {
  return (rows || []).flatMap((undernode) => {
    const row = {
      ...undernode,
      indent: indent + 1,
      avtaleId,
      bestillingIsAtTop,
      topNode: false,
      avtalenavn,
    };

    return [
      row,
      ...processRows(
        row.undernoder as PraksisavtaleSted[],
        row.indent,
        row.avtaleId,
        row.bestillingIsAtTop,
        avtalenavn,
      ),
    ];
  });
};

export const processAvtaleDtos = (
  avtaler: PraksisaktivitetAvtalerDto[],
): PraksisavtaleSted[] =>
  avtaler.flatMap((topnode) => {
    const avtaleRow = {
      ...topnode.praksissted,
      indent: 0,
      avtaleId: topnode.avtaleId,
      topNode: true,
      bestillingIsAtTop:
        topnode.bestillingsmodus === topnode.praksissted.bestillingspunkt,
      periode: topnode.periode,
      avtalenavn: topnode.avtalenavn,
    };

    return [
      avtaleRow,
      ...processRows(
        avtaleRow.undernoder as PraksisavtaleSted[],
        avtaleRow.indent,
        avtaleRow.avtaleId,
        avtaleRow.bestillingIsAtTop,
        avtaleRow.avtalenavn,
      ),
    ];
  });

export const getDownwardKapasitetspunkterInHierarchyFromIgnoringPathOf = (
  praksisstedHierarchyFromSted: FlattenedAvtaleSted,
  ignorePathOfPraksisstedAvtaleKey: string,
  ignorePathOfPraksissted: FlattenedAvtaleSted,
): string[] => {
  const path = ignorePathOfPraksissted.overnoder.concat(
    praksisstedIdFromAvtaleKey(ignorePathOfPraksisstedAvtaleKey),
  );

  return Object.entries(
    flattenAvtaleSteder(
      praksisstedHierarchyFromSted.undernoder,
      avtaleIdFromAvtaleKey(ignorePathOfPraksisstedAvtaleKey),
    ),
  )
    .filter(([avtaleKey]) =>
      path.includes(praksisstedIdFromAvtaleKey(avtaleKey)),
    )
    .flatMap(([_, praksissted]) => praksissted.soskennoder);
};

export const getAvtaleIdsToPraksisstedIdsFromAvtaleKeyEntryArray =
  (avtaleIdEntries: { [p: string]: [string, unknown][] }) =>
    Object.entries(avtaleIdEntries).reduce(
      (
        acc: PraksisavtaleIdToPraksisstedIderDto[],
        [avtaleId, steder]: [string, [string, unknown][]],
      ) =>
        acc.concat({
          avtaleId,
          praksisstedIder: steder.map(([avtaleKey]) =>
            praksisstedIdFromAvtaleKey(avtaleKey),
          ),
        }),
      [],
    );

export const avtaleKeyInListOfPraksisavtaleIdToPraksisstedIderDto = (
  avtaleKey: string,
  avtaleIdsToPraksissteder: PraksisavtaleIdToPraksisstedIderDto[],
): boolean =>
  !!avtaleIdsToPraksissteder
    .find((sted) => sted.avtaleId === avtaleIdFromAvtaleKey(avtaleKey))
    ?.praksisstedIder?.includes(praksisstedIdFromAvtaleKey(avtaleKey));

const initAgreementTableContext: AgreementTableContextProps = {
  avtaler: undefined,
  bestillingsmoduser: {},
  delt: false,
  expanded: {},
  haveOvernodeOrSiblingOrUndernodeWithFordeling: [],
  haveOvernodeOrSiblingOrUndernodeWithOnske: [],
  kanEndre: false,
  kanFlytteBestillinger: [],
  kanFlytteKapasitetTilSteder: [],
  state: {},
  praksisaktivitetId: '',
  addAvtalestedWithoutAdmin: () => {},
  flyttKapasitetspunktTil: () => {},
  handleDeaktivertPraksissted: () => {},
  handleSkjultPraksissted: () => {},
  onBestillingChange: () => {},
  onKapasitetChange: () => {},
  shouldDisplayPraksissted: () => false,
  toggleBestillingsmodus: () => {},
  toggleExpanded: () => {},
};

export const AgreementTableContext =
  React.createContext<AgreementTableContextProps>(initAgreementTableContext);

export const AgreementTableRowContext = React.createContext<
  AgreementTableRowProps | undefined
>(undefined);

export const useAgreementTableRowContext = () =>
  useContextNotUndefined(
    AgreementTableRowContext,
    'useAgreementTableRowContext',
  );
